import React from 'react';

// Site-wide animated backdrop for the public landing page.
//
// Three layers, all GPU-cheap and fixed to the viewport so they cost the
// same regardless of page length:
//
//   1. Aurora blobs — three large radial gradients drifting on a slow
//      x/y/scale loop. Tinted in the brand triad (cyan, deep blue, violet)
//      and held at low opacity so they read as ambient light, not posters.
//   2. Star field   — a single repeating dot-pattern background that
//      pans diagonally on a long loop. CSS-only, zero JS per frame.
//   3. Scanline veil — a 1px horizontal-line gradient at 0.025 alpha
//      sitting on top of everything to keep the surface from looking flat.
//
// The whole component is `pointer-events-none` so it never intercepts
// scroll/click events. Mounted once at the top of LandingPage, behind
// every section.
const LandingBackground = () => (
    <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10" aria-hidden="true">
        {/* Base wash — locks the brand bg under everything */}
        <div className="absolute inset-0 bg-[var(--bg-dark)]" />

        {/* Aurora blob 1 — cyan, top-left drift */}
        <div
            className="absolute -top-40 -left-40 w-[720px] h-[720px] rounded-full opacity-[0.18] blur-[140px] mix-blend-screen helix-bg-aurora-1"
            style={{ background: 'radial-gradient(circle, #18b5f0 0%, transparent 60%)' }}
        />
        {/* Aurora blob 2 — deep blue, bottom-right drift */}
        <div
            className="absolute -bottom-40 -right-40 w-[820px] h-[820px] rounded-full opacity-[0.22] blur-[160px] mix-blend-screen helix-bg-aurora-2"
            style={{ background: 'radial-gradient(circle, #0d52bc 0%, transparent 60%)' }}
        />
        {/* Aurora blob 3 — violet, mid drift */}
        <div
            className="absolute top-1/2 left-1/2 w-[600px] h-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-[0.12] blur-[120px] mix-blend-screen helix-bg-aurora-3"
            style={{ background: 'radial-gradient(circle, #a855f7 0%, transparent 60%)' }}
        />

        {/* Star field — diagonal pan loop. Layered tiny dots over a faint grid
             so the surface has structure without competing with the auroras. */}
        <div
            className="absolute inset-0 opacity-[0.45] helix-bg-stars"
            style={{
                backgroundImage:
                    'radial-gradient(rgba(255,255,255,0.18) 1px, transparent 1px), radial-gradient(rgba(255,255,255,0.10) 1px, transparent 1px)',
                backgroundSize: '120px 120px, 200px 200px',
                backgroundPosition: '0 0, 60px 60px',
            }}
        />

        {/* Subtle 60px grid — keeps the eye anchored when blobs drift */}
        <div
            className="absolute inset-0 opacity-[0.4]"
            style={{
                backgroundImage:
                    'linear-gradient(rgba(255,255,255,0.018) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.018) 1px, transparent 1px)',
                backgroundSize: '60px 60px',
            }}
        />

        {/* Scanline veil — gives the surface a faint CRT haze */}
        <div
            className="absolute inset-0 opacity-[0.025]"
            style={{
                backgroundImage: 'repeating-linear-gradient(0deg, rgba(255,255,255,0.5) 0 1px, transparent 1px 3px)',
            }}
        />

        {/* Top + bottom edge fades — soften the blobs at viewport boundaries */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-[var(--bg-dark)] to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[var(--bg-dark)] to-transparent" />

        {/* Local keyframes — kept inline so the component is fully portable
             and there's no global CSS coupling to remember. The animations
             are slow on purpose; fast motion in a backdrop draws the eye
             away from the actual content. */}
        <style>{`
            @keyframes helix-aurora-1 {
                0%, 100% { transform: translate(0, 0) scale(1); }
                33%      { transform: translate(120px, 80px) scale(1.15); }
                66%      { transform: translate(-60px, 140px) scale(0.95); }
            }
            @keyframes helix-aurora-2 {
                0%, 100% { transform: translate(0, 0) scale(1); }
                40%      { transform: translate(-160px, -100px) scale(1.1); }
                75%      { transform: translate(80px, -180px) scale(1.05); }
            }
            @keyframes helix-aurora-3 {
                0%, 100% { transform: translate(-50%, -50%) scale(1); }
                50%      { transform: translate(-40%, -60%) scale(1.25); }
            }
            @keyframes helix-stars-drift {
                from { background-position: 0 0, 60px 60px; }
                to   { background-position: 240px 240px, 300px 300px; }
            }
            .helix-bg-aurora-1 { animation: helix-aurora-1 28s ease-in-out infinite; }
            .helix-bg-aurora-2 { animation: helix-aurora-2 36s ease-in-out infinite; }
            .helix-bg-aurora-3 { animation: helix-aurora-3 42s ease-in-out infinite; }
            .helix-bg-stars    { animation: helix-stars-drift 90s linear infinite; }
            @media (prefers-reduced-motion: reduce) {
                .helix-bg-aurora-1, .helix-bg-aurora-2, .helix-bg-aurora-3, .helix-bg-stars {
                    animation: none;
                }
            }
        `}</style>
    </div>
);

export default LandingBackground;
